import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, Shield, ChevronDown, MessageCircle, Menu } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigationStore } from '@/store';
import { useAuth } from '@/providers/auth-provider';
import { usePermissions } from '@/lib/permissions';
import { NAV_ITEMS, ROUTES } from '@/utils/constants';
import { cn, openAskSanjoyWidget } from '@/utils';
import { useNavProducts } from '@/hooks';
import { SJMegaMenu } from './sj-mega-menu';

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { openMobileMenu, isMobileMenuOpen } = useNavigationStore();
  const { user, isAuthenticated, signOut } = useAuth();
  const { canAccessWorkspace, isAdmin } = usePermissions();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const megaMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: products = [], isLoading: productsLoading } = useNavProducts();

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 8);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setDropdownOpen(false);
    setActiveMegaMenu(null);
  }, [location.pathname]);

  const handleMegaMenuEnter = useCallback((key: string) => {
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    setActiveMegaMenu(key);
  }, []);

  const handleMegaMenuLeave = useCallback(() => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 150);
  }, []);

  async function handleSignOut() {
    setDropdownOpen(false);
    await signOut();
    toast.success('Signed out.');
    navigate('/');
  }

  const initials = user
    ? (user.name ?? user.email)
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '';

  function getMegaMenuData(key: string) {
    if (key === 'products') {
      return {
        items: products.map((p) => ({
          slug: p.slug,
          name: p.title,
          description: p.tagline,
          icon: p.icon,
          logoUrl: p.logo_url,
          color: p.color,
        })),
        isLoading: productsLoading,
        basePath: ROUTES.PRODUCTS,
        viewAllLabel: 'View All Products',
        columns: 2 as const,
      };
    }
    return { items: [], isLoading: false, basePath: '/', viewAllLabel: 'View All', columns: 3 as const };
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-[100] w-full transition-all duration-200',
        isScrolled
          ? 'border-b border-[var(--sj-border)] bg-[var(--sj-background)]/95 backdrop-blur-lg shadow-sm'
          : 'border-b border-transparent bg-[var(--sj-background)]/60 backdrop-blur-md'
      )}
    >
      <nav className="mx-auto flex h-16 max-w-[1280px] items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2">
          <img src="/sj_light.png" alt="Sanjoy" className="h-9 w-auto" />
        </Link>

        <div className="hidden items-center gap-0.5 lg:flex">
          {NAV_ITEMS.map((item) => (
            <div
              key={item.path}
              className="relative"
              onMouseEnter={() => item.megaMenuKey && handleMegaMenuEnter(item.megaMenuKey)}
              onMouseLeave={handleMegaMenuLeave}
            >
              <Link
                to={item.path}
                className={cn(
                  'flex items-center gap-1 rounded-[8px] px-3 py-2 text-sm font-medium transition-colors',
                  location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                    ? 'bg-[var(--sj-surface-muted)] text-[var(--sj-text-primary)]'
                    : 'text-[var(--sj-text-secondary)] hover:text-[var(--sj-text-primary)] hover:bg-[var(--sj-surface-muted)]'
                )}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                aria-haspopup={item.megaMenuKey ? 'menu' : undefined}
                aria-expanded={item.megaMenuKey ? activeMegaMenu === item.megaMenuKey : undefined}
              >
                {item.label}
                {item.megaMenuKey && (
                  <ChevronDown className={cn('h-3 w-3 transition-transform', activeMegaMenu === item.megaMenuKey && 'rotate-180')} />
                )}
              </Link>

              {item.megaMenuKey && (() => {
                const menuData = getMegaMenuData(item.megaMenuKey);
                return (
                  <SJMegaMenu
                    isOpen={activeMegaMenu === item.megaMenuKey}
                    onClose={() => setActiveMegaMenu(null)}
                    items={menuData.items}
                    isLoading={menuData.isLoading}
                    basePath={menuData.basePath}
                    viewAllLabel={menuData.viewAllLabel}
                    columns={menuData.columns}
                  />
                );
              })()}
            </div>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <button
            type="button"
            onClick={openAskSanjoyWidget}
            className="rounded-[10px] bg-[var(--sj-text-primary)] px-4 py-2 text-sm font-medium text-[var(--sj-background)] transition-opacity hover:opacity-90"
          >
            Ask Sanjoy
          </button>

          {isAuthenticated && user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 rounded-[10px] border border-[var(--sj-border)] px-2.5 py-1.5 transition-colors hover:border-primary-300 hover:bg-[var(--sj-surface-muted)]"
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                aria-label="User menu"
              >
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[0.65rem] font-semibold text-primary-700">
                    {initials}
                  </div>
                )}
                <ChevronDown className={cn('h-3 w-3 text-[var(--sj-text-muted)] transition-transform', dropdownOpen && 'rotate-180')} />
              </button>

              {dropdownOpen && (
                <div role="menu" className="absolute right-0 top-full mt-2 w-52 rounded-[12px] border border-[var(--sj-border)] bg-[var(--sj-surface)] p-1.5 shadow-lg">
                  <div className="mb-1.5 border-b border-[var(--sj-border)] px-3 pb-2 pt-1">
                    <p className="text-sm font-medium text-[var(--sj-text-primary)]">{user.name ?? 'User'}</p>
                    <p className="text-xs text-[var(--sj-text-muted)]">{user.email}</p>
                  </div>

                  <Link
                    to="/profile"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-2.5 rounded-[8px] px-3 py-2 text-sm text-[var(--sj-text-secondary)] transition-colors hover:bg-[var(--sj-surface-muted)] hover:text-[var(--sj-text-primary)]"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </Link>

                  <Link
                    to="/profile"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-2.5 rounded-[8px] px-3 py-2 text-sm text-[var(--sj-text-secondary)] transition-colors hover:bg-[var(--sj-surface-muted)] hover:text-[var(--sj-text-primary)]"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>

                  {canAccessWorkspace && (
                    <Link
                      to={isAdmin ? '/admin' : '/admin/knowledge'}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 rounded-[8px] px-3 py-2 text-sm text-[var(--sj-text-secondary)] transition-colors hover:bg-[var(--sj-surface-muted)] hover:text-[var(--sj-text-primary)]"
                    >
                      <Shield className="h-4 w-4" />
                      {isAdmin ? 'Admin' : 'Knowledge Workspace'}
                    </Link>
                  )}

                  <div className="my-1 border-t border-[var(--sj-border)]" />

                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2.5 rounded-[8px] px-3 py-2 text-sm text-error-600 transition-colors hover:bg-error-50"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to={ROUTES.LOGIN}
              className="flex items-center gap-1.5 rounded-[8px] px-3 py-2 text-sm font-medium text-[var(--sj-text-secondary)] transition-colors hover:text-[var(--sj-text-primary)]"
            >
              <User className="h-4 w-4" />
              Sign In
            </Link>
          )}
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <button
            type="button"
            onClick={() => openAskSanjoyWidget()}
            className="touch-target inline-flex items-center justify-center rounded-[8px] text-[var(--sj-text-secondary)] hover:bg-[var(--sj-surface-muted)]"
            aria-label="Ask Sanjoy"
          >
            <MessageCircle className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={openMobileMenu}
            className="touch-target inline-flex items-center justify-center rounded-[8px] text-[var(--sj-text-secondary)] hover:bg-[var(--sj-surface-muted)]"
            aria-label="More menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>
    </header>
  );
}
